'use client';

/**
 * Per-stage live progress strip for the create-proposal dialog. Subscribes
 * to the `CreateMarketProgressState` exposed by `useCreateProposalMutation`
 * and renders a vertical checklist as each SSE event lands.
 *
 *   [✓] Preparing metadata
 *   [✓] Pinning to IPFS
 *   [✓] Creating market on chain      shell signature: 4xK…
 *   [●] Initializing outcomes (2/3)
 *   [ ] Finalizing
 *
 * On terminal `error` the strip stays mounted but the dialog wrapper
 * swaps in a failure-view footer with a Retry button (autoclean already
 * ran server-side, so retry is a fresh submission).
 */
import { c as _c } from "react/compiler-runtime";
import { Icon } from '@/components/ui/icon';
import { Cancel01Icon, Loading03Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import type { CreateMarketProgressState } from '@/hooks/proposals/use-create-proposal';
import { cn } from '@/lib/ui/utils';
type StageStatus = 'idle' | 'active' | 'done' | 'failed';
interface StageRowProps {
  status: StageStatus;
  label: string;
  detail?: string;
}
function StageRow(t0) {
  const $ = _c(28);
  const {
    status,
    label,
    detail
  } = t0;
  const t1 = status === "done" && "bg-feedback-success-accent/15 text-feedback-success-content";
  const t2 = status === "active" && "bg-background-brand text-foreground-brand";
  const t3 = status === "idle" && "bg-background-tertiary text-foreground-tertiary";
  const t4 = status === "failed" && "bg-feedback-error-accent/15 text-feedback-error-content";
  let t5;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = cn("inline-flex size-5 shrink-0 items-center justify-center rounded-full", t1, t2, t3, t4);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== status) {
    t6 = status === "done" && <Icon icon={Tick02Icon} className="size-3" />;
    $[5] = status;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== status) {
    t7 = status === "active" && <Icon icon={Loading03Icon} className="size-3 animate-spin" />;
    $[7] = status;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== status) {
    t8 = status === "failed" && <Icon icon={Cancel01Icon} className="size-3" />;
    $[9] = status;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = <span className={t5}>{t6}{t7}{t8}</span>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = status === "idle" && "text-foreground-tertiary";
  const t11 = status === "failed" && "text-feedback-error-content";
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = cn("flex-1 min-w-0 truncate", t10, t11);
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== label || $[20] !== t12) {
    t13 = <span className={t12}>{label}</span>;
    $[19] = label;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== detail) {
    t14 = detail !== undefined && detail.length > 0 && <span className="text-caption font-mono text-foreground-tertiary">{detail}</span>;
    $[22] = detail;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t13 || $[25] !== t14 || $[26] !== t9) {
    t15 = <div className="flex items-center gap-2 text-body">{t9}{t13}{t14}</div>;
    $[24] = t13;
    $[25] = t14;
    $[26] = t9;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  return t15;
}
function shortenSignature(sig: string | null): string {
  if (sig === null || sig.length < 12) return sig ?? '';
  return `${sig.slice(0, 6)}…${sig.slice(-4)}`;
}
export interface CreateMarketProgressStripProps {
  progress: CreateMarketProgressState;
}
export function CreateMarketProgressStrip(t0) {
  const $ = _c(24);
  const {
    progress
  } = t0;
  const isFailed = progress.status === "failed";
  const failedStage = isFailed ? progress.error?.stage ?? null : null;
  const prepStatus = failedStage === "preparing" ? "failed" : progress.status === "idle" ? "idle" : progress.status === "preparing" ? "active" : "done";
  const shellStatus = failedStage === "shell" ? "failed" : progress.shellSubmitted === null ? progress.status === "shell" ? "active" : "idle" : progress.shellConfirmed ? "done" : "active";
  const outcomesStatus = failedStage === "outcomes" ? "failed" : !progress.shellConfirmed ? "idle" : progress.outcomesConfirmed >= progress.outcomesTotal && progress.outcomesTotal > 0 ? "done" : "active";
  const finalizeStatus = failedStage === "finalize" ? "failed" : progress.outcomesConfirmed < progress.outcomesTotal || progress.outcomesTotal === 0 ? "idle" : progress.finalizeConfirmed ? "done" : "active";
  let t1;
  if ($[0] !== prepStatus) {
    t1 = <StageRow status={prepStatus} label="Preparing market" />;
    $[0] = prepStatus;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== progress.shellSubmitted) {
    t2 = shortenSignature(progress.shellSubmitted);
    $[2] = progress.shellSubmitted;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== shellStatus || $[5] !== t2) {
    t3 = <StageRow status={shellStatus} label="Creating market on chain" detail={t2} />;
    $[4] = shellStatus;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = progress.outcomesTotal === 0 ? "Initializing outcomes" : `Initializing outcomes (${String(progress.outcomesConfirmed)}/${String(progress.outcomesTotal)})`;
  let t5;
  if ($[7] !== outcomesStatus || $[8] !== t4) {
    t5 = <StageRow status={outcomesStatus} label={t4} />;
    $[7] = outcomesStatus;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== progress.finalizeSubmitted) {
    t6 = shortenSignature(progress.finalizeSubmitted);
    $[10] = progress.finalizeSubmitted;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== finalizeStatus || $[13] !== t6) {
    t7 = <StageRow status={finalizeStatus} label="Finalizing" detail={t6} />;
    $[12] = finalizeStatus;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isFailed || $[16] !== progress.error) {
    t8 = isFailed && progress.error !== null && <p className="rounded-overlay-item border bg-background-primary p-3 text-body text-feedback-error-content">{progress.error.message}</p>;
    $[15] = isFailed;
    $[16] = progress.error;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t1 || $[19] !== t3 || $[20] !== t5 || $[21] !== t7 || $[22] !== t8) {
    t9 = <div className="flex flex-col gap-3 rounded-panel-row border border-border-primary bg-background-tertiary p-4">{t1}{t3}{t5}{t7}{t8}</div>;
    $[18] = t1;
    $[19] = t3;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}